import type { NewsItem as INewsItem } from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import {
  getArchiveForYear,
  getArchiveYears,
} from '@bulknutrients/bulk-shared-lib/lib/sanity/newsPages';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts';
import { NewsItem } from '@components/News/NewsItem';
import { convertNewsArticleToStructuredData } from '@lib/structured';
import type { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

interface Props {
  breadcrumbs: { title: string; path: string }[];
  byMonth: Record<string, INewsItem[]>;
  year: string;
  structuredData: Array<any>;
}

export default function NewsArchiveYearPage({
  breadcrumbs,
  byMonth,
  year,
  structuredData,
}: Props): ReactElement {
  return (
    <>
      <Head>
        <title>The latest updates in Bulk Nutrients {year} News Archive</title>
        <meta
          name="title"
          content={`The latest updates in Bulk Nutrients ${year} News Archive`}
        />
        <meta
          name="description"
          content={`Dive into what happened at Bulk in ${year}. New flavours, ambassadors, product launches and discontinued products all listed in our news archive.`}
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/news/${year}`}
        />
        {structuredData.map((item) => (
          <script
            key={item.headline}
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(item),
            }}
          />
        ))}
      </Head>
      <Container>
        <Breadcrumbs crumbs={breadcrumbs} />
      </Container>
      <Container
        as="main"
        id="main-content"
        className="mx-auto my-8 max-w-narrow-page"
      >
        <h1>Bulk Nutrients {year} News</h1>
        {Object.keys(byMonth).map((month) => (
          <article className="pb-10" key={month}>
            <h2 className="italic" id={`#${month.toLowerCase()}`}>
              {month} {year} News
            </h2>
            {byMonth[month].map((item) => (
              <NewsItem
                key={item.title}
                item={item}
                baseUrl={`https://www.bulknutrients.com.au/news/${year}`}
              />
            ))}
          </article>
        ))}
      </Container>
    </>
  );
}

NewsArchiveYearPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  // Turn them into param paths
  const years = await getArchiveYears();
  const paths = years.map((year) => ({
    params: {
      year: year,
    },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const breadcrumbs = [
    { title: 'home', path: '/' },
    { title: 'Bulk Nutrients News', path: '/news/' },
  ];

  if (params && params.year && typeof params.year === 'string') {
    breadcrumbs.push({ title: params.year, path: `/news/${params.year}` });
    // const structuredData = [] as Array<any>;

    const archive = await getArchiveForYear(params.year);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'Australia/Hobart',
      timeZoneName: 'long',
    };
    const { byMonth, structuredData } = archive.reduce(
      (acc, cur) => {
        const month = new Date(cur.datetime)
          .toLocaleDateString('en-AU', options)
          .split(' ')[1];
        if (acc.byMonth[month]) {
          acc.byMonth[month].push(cur);
        } else {
          acc.byMonth[month] = [cur];
        }
        acc.structuredData.push(
          convertNewsArticleToStructuredData(
            cur,
            process.env.NEXT_PUBLIC_SERVER_URL || ''
          )
        );
        return acc;
      },
      { byMonth: {}, structuredData: [] as Array<any> }
    );

    Object.keys(byMonth).forEach((month) => {
      byMonth[month].sort((a, b) => (a.pinned ? -1 : 1));
    });

    return {
      props: {
        breadcrumbs,
        byMonth,
        year: params.year.toString(),
        structuredData,
      },
    };
  }

  // failed
  return {
    notFound: true,
  };
};
